import type { WabunkaTag } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_tag';

/**
 * Wabunka タグビューモデル
 */
export interface WabunkaTagViewModel {
  /** ID */
  id: string;
  /** タグ名 */
  name: string;
  /** パス */
  webPath: string;
  // 画像パス
  imgPath: string;
  // タググループ名（後方互換: 先頭グループ。複数所属タグの判定には tagGroups を使う）
  tagGroupName: string;
  // タググループタイプ（後方互換: 先頭グループ）
  tagGroupType: string;
  // 全所属タググループ（JCAT-4637）。多重所属タグの過剰除外を防ぐためにバックエンドが全件返す。
  // テストデータ等のリテラル構築の互換性のため optional（実行時は buildWabunkaTagViewModel が必ず設定）。
  tagGroups?: { name: string; type: string }[];
}

/**
 * Wabunka タグビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaTagViewModel} - Wabunka タグビューモデル のデフォルト値
 */
export const createDefaultWabunkaTagViewModel = (): WabunkaTagViewModel => ({
  id: '',
  name: '',
  webPath: '',
  imgPath: '',
  tagGroupName: '',
  tagGroupType: '',
  tagGroups: [],
});

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaTag} res - APIレスポンス
 * @returns {WabunkaTagViewModel} - ビューモデル
 */
export const buildWabunkaTagViewModel = (res: WabunkaTag | undefined): WabunkaTagViewModel => {
  if (!res) return createDefaultWabunkaTagViewModel();
  return {
    id: res.id,
    name: res.name,
    webPath: res.webPath,
    imgPath: res.imgPath,
    tagGroupName: res.tagGroupName,
    tagGroupType: res.tagGroupType,
    // proto 新フィールド。旧 backend（未充填）時は空配列フォールバック
    tagGroups: res.tagGroups?.map((g) => ({ name: g.name, type: g.type })) ?? [],
  };
};

/**
 * リスト表示用のタグビューモデル
 */
export type WabunkaTagListViewModel = WabunkaTagViewModel[];

/**
 * 辞書型のリスト表示用のタグビューモデル
 * { [tagGroupName]: WabunkaTagListViewModel }
 */
export type WabunkaTagDictByGroupName = Record<string, WabunkaTagListViewModel>;

/**
 * 辞書型のタグビューモデル
 * { [webPath]: WabunkaTagViewModel }
 */
export type WabunkaTagDict = Record<string, WabunkaTagViewModel>;

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildWabunkaTagListViewModel = (tags: WabunkaTag[] | undefined): WabunkaTagListViewModel => {
  if (!tags) {
    return [];
  }

  return tags.map(buildWabunkaTagViewModel);
};
